/**
 * Cricket Game - Canvas-based NES-style cricket renderer
 *
 * Renders the cricket match with sprite animations on a canvas element.
 * All movement is position-based with frame-by-frame animation.
 */

import {
  createPlayerSpriteSheet,
  createKeeperSpriteSheet,
  createBallSprite,
  createWicketSprite,
  CricketPlayer,
  SPRITE_WIDTH,
  SPRITE_HEIGHT,
  SCALE,
  COLORS
} from './cricket-sprites.js';

import {
  getCurrentMatchState,
  formatOvers,
  shotDirections
} from './cricket.js';

// Game constants - zoomed out view
const PITCH_WIDTH = 400;
const PITCH_HEIGHT = 280;
const CANVAS_SCALE = 2;

// Field positions (in game coordinates) - all within boundary
// Batsman stands IN FRONT of wicket (between bowler and wicket)
const FIELD_POSITIONS = {
  bowlerStart: { x: 70, y: 140 },
  bowlerEnd: { x: 115, y: 140 },
  bowlerWicket: { x: 125, y: 140 },      // Bowler's end wicket (moved back)
  strikerWicket: { x: 275, y: 140 },     // Striker's end wicket (moved back)
  strikerCrease: { x: 260, y: 140 },     // Batsman closer to wicket
  nonStrikerCrease: { x: 138, y: 140 },  // Non-striker closer to bowler's wicket
  keeper: { x: 295, y: 140 },            // Keeper closer behind wicket
  slip: { x: 310, y: 105 },
  gully: { x: 295, y: 85 },
  point: { x: 270, y: 60 },
  cover: { x: 200, y: 55 },
  midOff: { x: 130, y: 60 },
  midOn: { x: 130, y: 220 },
  midWicket: { x: 200, y: 225 },
  squareLeg: { x: 270, y: 220 },
  fineLeg: { x: 310, y: 180 }
};

// Colors for NES-style grass
const GRASS_LIGHT = '#30b010';
const GRASS_DARK = '#208000';
const PITCH_COLOR = '#c9b896';
const PITCH_DARK = '#a08050';

// Stadium colors
const STAND_COLOR = '#484858';
const STAND_DARK = '#303040';
const STAND_LIGHT = '#606070';
const STAND_TIER = '#3a3a4a';
const CROWD_COLORS = ['#e05050', '#5050e0', '#e0e050', '#50e050', '#e09030', '#9030e0', '#30e0e0', '#e0e0e0', '#e07070', '#7070e0', '#50a050', '#e0a050'];
const BOUNDARY_COLOR = '#ffffff';
const BANNER_COLORS = ['#0040c0', '#c00040', '#00a040', '#c08000'];
const BANNER_TEXTS = ['RADIO 1', 'BBC SOUNDS', 'CRICKET', 'LIVE'];
const ROOF_COLOR = '#252535';
const STRUCTURE_COLOR = '#1a1a2a';

// Exterior colors (car park, buildings, sky)
const SKY_COLOR = '#4a6090';
const SKY_HORIZON = '#7090b0';
const BUILDING_COLORS = ['#404050', '#505060', '#454555', '#3a3a4a', '#484858'];
const CARPARK_COLOR = '#2a2a30';
const CARPARK_LINE = '#3a3a40';
const CAR_COLORS = ['#c03030', '#3030c0', '#30a030', '#e0e0e0', '#202020', '#c0c030', '#a04080', '#40a0a0'];
const TREE_TRUNK = '#4a3020';
const TREE_LEAVES = ['#206020', '#308030', '#287028'];

// Boundary limits (players must stay inside these)
const BOUNDARY = {
  left: 45,
  right: 355,
  top: 50,
  bottom: 250
};

class CricketGame {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.ctx.imageSmoothingEnabled = false;

    // Game area dimensions (the stadium view)
    this.gameWidth = PITCH_WIDTH * CANVAS_SCALE;
    this.gameHeight = PITCH_HEIGHT * CANVAS_SCALE;

    // Canvas fills container - will be set by resize
    this.width = this.gameWidth;
    this.height = this.gameHeight;
    this.offsetX = 0; // Offset to center game area
    this.offsetY = 0;

    // Set up resize handling
    this.resizeCanvas();
    this.resizeHandler = () => this.resizeCanvas();
    window.addEventListener('resize', this.resizeHandler);

    // Timing
    this.lastTime = 0;
    this.animationFrame = null;

    // Game state
    this.matchState = null;
    this.lastBallIndex = -1;
    this.isAnimating = false;
    this.pendingTimeouts = [];

    // Sprites
    this.sprites = {};
    this.players = {};

    // Ball state machine
    this.ball = {
      visible: false,
      x: 0,
      y: 0,
      startX: 0,
      startY: 0,
      targetX: 0,
      targetY: 0,
      progress: 0,      // 0 to 1
      duration: 0,      // ms
      phase: 'idle'     // 'idle', 'bowling', 'toFielder', 'toBoundary'
    };

    // Wickets
    this.wicketIntact = true;
    this.wicketSprite = null;
    this.wicketBrokenSprite = null;

    // Flash text
    this.flashText = '';
    this.flashTimer = 0;
    this.flashColor = '#fff';

    // Current ball data for animation
    this.currentBallData = null;

    // Crowd animation state
    this.crowdExcitement = 0; // 0-1, increases on boundaries/wickets
    this.gameTime = 0; // For crowd animation

    // Weather state
    this.weatherType = 'clear'; // 'clear', 'rain'
    this.sunGlare = 0;
    this.initWeather();

    // Banner animation
    this.bannerOffset = 0;
    this.bannerTexts = ['RADIO 1', 'BBC SOUNDS', 'CRICKET LEAGUE', 'LIVE', 'BBC', 'GREG JAMES', 'TUNE IN', '#R1CRICKET'];

    // Easter eggs
    this.easterEgg = null;
    this.easterEggTimer = 0;

    // Celebrations
    this.celebration = null;
    this.celebrationTimer = 0;

    // Highlights tracking
    this.highlights = [];

    // Initialize
    this.init();
  }

  initWeather() {
    const seed = Date.now();
    let weatherRand = seed;
    const nextRand = () => {
      weatherRand = (weatherRand * 9301 + 49297) % 233280;
      return weatherRand / 233280;
    };

    // Determine weather - just rain or clear with sun
    const hour = new Date().getHours();
    if (nextRand() < 0.1) {
      this.weatherType = 'rain';
    } else {
      this.weatherType = 'clear';
    }

    // Sun glare in afternoon (when not raining)
    if (hour >= 14 && hour <= 17 && this.weatherType === 'clear') {
      this.sunGlare = 0.3 + nextRand() * 0.3;
    }
  }

  resizeCanvas() {
    // Get container size
    const rect = this.canvas.getBoundingClientRect();
    const containerWidth = rect.width || window.innerWidth;
    const containerHeight = rect.height || 400;

    // Use device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;

    // Set canvas to fill container
    this.width = containerWidth * dpr;
    this.height = containerHeight * dpr;
    this.canvas.width = this.width;
    this.canvas.height = this.height;

    // Calculate scale to fit game area while maintaining aspect ratio
    const gameAspect = this.gameWidth / this.gameHeight;
    const containerAspect = this.width / this.height;

    if (containerAspect > gameAspect) {
      // Container is wider - fit to height, center horizontally
      this.scale = this.height / this.gameHeight;
      this.offsetX = (this.width - this.gameWidth * this.scale) / 2;
      this.offsetY = 0;
    } else {
      // Container is taller - fit to width, center vertically
      this.scale = this.width / this.gameWidth;
      this.offsetX = 0;
      this.offsetY = (this.height - this.gameHeight * this.scale) / 2;
    }

    this.ctx.imageSmoothingEnabled = false;
  }

  async init() {
    // Create sprites
    this.ballSprite = createBallSprite();
    this.wicketSprite = createWicketSprite(true);
    this.wicketBrokenSprite = createWicketSprite(false);

    // Get initial match state to set up teams
    this.matchState = getCurrentMatchState();

    // Create team sprite sheets
    const battingColor = this.matchState.battingTeam.color;
    const bowlingColor = this.matchState.bowlingTeam.color;

    this.sprites.battingTeam = createPlayerSpriteSheet(battingColor, COLORS.hairBrown);
    this.sprites.bowlingTeam = createPlayerSpriteSheet(bowlingColor, COLORS.hairBlack);
    this.sprites.keeper = createKeeperSpriteSheet(bowlingColor, COLORS.hairBlack);

    // Create players
    this.createPlayers();

    // Start game loop
    this.lastTime = performance.now();
    this.gameLoop(this.lastTime);

    // Start match state polling
    this.pollMatchState();
  }

  createPlayers() {
    const battingSheet = this.sprites.battingTeam;
    const bowlingSheet = this.sprites.bowlingTeam;

    // Batsmen
    this.players.striker = new CricketPlayer(battingSheet, FIELD_POSITIONS.strikerCrease.x, FIELD_POSITIONS.strikerCrease.y, 'batsman');
    this.players.striker.direction = 'left';
    this.players.striker.animator.play('batReady'); // Batting stance with bat

    this.players.nonStriker = new CricketPlayer(battingSheet, FIELD_POSITIONS.nonStrikerCrease.x, FIELD_POSITIONS.nonStrikerCrease.y, 'batsman');
    this.players.nonStriker.direction = 'right';
    this.players.nonStriker.animator.play('idleRight');

    // Bowler
    this.players.bowler = new CricketPlayer(bowlingSheet, FIELD_POSITIONS.bowlerStart.x, FIELD_POSITIONS.bowlerStart.y, 'bowler');
    this.players.bowler.direction = 'right';
    this.players.bowler.animator.play('idleRight');

    // Keeper (uses special sprite sheet - for now use regular)
    this.players.keeper = new CricketPlayer(bowlingSheet, FIELD_POSITIONS.keeper.x, FIELD_POSITIONS.keeper.y, 'keeper');
    this.players.keeper.direction = 'left';
    this.players.keeper.animator.play('idleLeft');

    // Fielders
    const fielderPositions = ['slip', 'gully', 'point', 'cover', 'midOff', 'midOn', 'midWicket', 'squareLeg', 'fineLeg'];
    fielderPositions.forEach(pos => {
      const p = FIELD_POSITIONS[pos];
      this.players[pos] = new CricketPlayer(bowlingSheet, p.x, p.y, 'fielder');
      // Face towards pitch center (center is at x=200)
      this.players[pos].direction = p.x > 200 ? 'left' : 'right';
      this.players[pos].animator.play(p.x > 200 ? 'idleLeft' : 'idleRight');
    });

    // Set boundary on all players
    Object.values(this.players).forEach(player => {
      player.setBoundary(BOUNDARY);
    });
  }

  pollMatchState() {
    setInterval(() => {
      this.matchState = getCurrentMatchState();
      this.checkForNewBall();
    }, 1000);
  }

  checkForNewBall() {
    if (!this.matchState || !this.matchState.currentBall) return;

    const ball = this.matchState.currentBall;
    const ballIndex = ball.over * 6 + ball.ball;

    if (ballIndex !== this.lastBallIndex && !this.isAnimating) {
      this.lastBallIndex = ballIndex;
      this.animateBall(ball);
    }
  }

  // Helper to schedule timeouts that can be cancelled
  scheduleTimeout(callback, delay) {
    const id = setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter(t => t !== id);
      callback();
    }, delay);
    this.pendingTimeouts.push(id);
    return id;
  }

  clearAllTimeouts() {
    this.pendingTimeouts.forEach(id => clearTimeout(id));
    this.pendingTimeouts = [];
  }

  animateBall(ballData) {
    // Clear any pending animations
    this.clearAllTimeouts();

    this.isAnimating = true;
    this.wicketIntact = true;
    this.currentBallData = ballData;

    // Reset ball state
    this.ball.visible = false;
    this.ball.phase = 'idle';
    this.ball.progress = 0;

    // Reset bowler position
    this.players.bowler.x = FIELD_POSITIONS.bowlerStart.x;
    this.players.bowler.y = FIELD_POSITIONS.bowlerStart.y;

    // Start animation sequence
    this.animateBowlerRunIn();
  }

  animateBowlerRunIn() {
    this.players.bowler.speed = 150;
    this.players.bowler.moveTo(FIELD_POSITIONS.bowlerEnd.x, FIELD_POSITIONS.bowlerEnd.y, () => {
      this.animateBowlerAction();
    });
  }

  animateBowlerAction() {
    this.players.bowler.playAction('bowl', () => {
      // Ball released - start bowling phase
      this.startBallBowling();
    });
  }

  startBallBowling() {
    const ball = this.currentBallData;
    if (!ball) return;

    // Calculate final destination based on result
    const result = ball.result;
    const shotDir = ball.shotDirection || 'cover';
    const targetPos = this.getShotTargetPosition(shotDir, result);

    // Store for later use
    this.ball.finalTarget = targetPos;
    this.ball.result = result;
    this.ball.shotDir = shotDir;

    // Set up ball trajectory - bowl to batsman (pitch near the crease)
    this.ball.startX = FIELD_POSITIONS.bowlerEnd.x + 10;
    this.ball.startY = FIELD_POSITIONS.bowlerEnd.y;
    this.ball.midX = FIELD_POSITIONS.strikerCrease.x - 15; // Pitch the ball short of batsman
    this.ball.midY = FIELD_POSITIONS.strikerCrease.y;
    this.ball.targetX = this.ball.midX;
    this.ball.targetY = this.ball.midY;
    this.ball.x = this.ball.startX;
    this.ball.y = this.ball.startY;
    this.ball.progress = 0;
    this.ball.duration = 280; // Faster bowling
    this.ball.phase = 'bowling';
    this.ball.visible = true;
  }

  onBallReachedBatsman() {
    const ball = this.currentBallData;
    if (!ball) return;

    // Start batsman swing animation (doesn't block ball movement)
    this.players.striker.playAction('bat');

    // Immediately continue ball to its destination - no pause!
    this.handleBallResult();
  }

  handleBallResult() {
    const ball = this.currentBallData;
    if (!ball) return;

    const result = this.ball.result;
    const shotDir = this.ball.shotDir;
    const targetPos = this.ball.finalTarget;

    if (result === 'WICKET') {
      // Ball hits wicket - short travel then stop
      this.ball.phase = 'idle';
      this.wicketIntact = false;
      this.showFlash('W', '#ff0000');
      this.triggerCelebration('wicket', ball.bowler);

      this.scheduleTimeout(() => {
        this.ball.visible = false;
      }, 200);

      this.scheduleTimeout(() => {
        this.resetAfterBall();
      }, 1500);

    } else if (result === 'SIX') {
      // Ball flies to boundary fast
      this.startBallToTarget(targetPos.x, targetPos.y, 500, 'toBoundary');
      this.showFlash('6', '#f8d800');
      this.triggerCelebration('six', ball.batsman);

      this.scheduleTimeout(() => {
        this.ball.visible = false;
        this.resetAfterBall();
      }, 700);

    } else if (result === 'FOUR') {
      // Ball races to boundary
      this.startBallToTarget(targetPos.x, targetPos.y, 600, 'toBoundary');
      this.showFlash('4', '#0058f8');
      this.chaseFielder(shotDir, targetPos);

      this.scheduleTimeout(() => {
        this.ball.visible = false;
        this.resetAfterBall();
      }, 800);

    } else if (ball.runs > 0) {
      // Ball to fielder, batsmen run
      this.startBallToTarget(targetPos.x, targetPos.y, 450, 'toFielder');
      this.showFlash(ball.runs.toString(), '#ffffff');
      this.chaseFielder(shotDir, targetPos);

      this.scheduleTimeout(() => {
        this.animateRunning(ball.runs);
      }, 250);

      this.scheduleTimeout(() => {
        this.ball.visible = false;
        this.resetAfterBall();
      }, 500 + ball.runs * 450);

    } else {
      // Dot ball - through to keeper
      this.startBallToTarget(FIELD_POSITIONS.keeper.x - 5, FIELD_POSITIONS.keeper.y, 350, 'toFielder');

      this.scheduleTimeout(() => {
        this.ball.visible = false;
        this.resetAfterBall();
      }, 500);
    }
  }

  startBallToTarget(targetX, targetY, duration, phase) {
    // Continue from current position smoothly
    this.ball.startX = this.ball.x;
    this.ball.startY = this.ball.y;
    this.ball.targetX = targetX;
    this.ball.targetY = targetY;
    this.ball.progress = 0;
    this.ball.duration = duration;
    this.ball.phase = phase;
  }

  getShotTargetPosition(shotDir, result) {
    // Map shot direction to screen position (within boundary)
    // Ball is hit from striker, directions relative to that
    const directionMap = {
      cover: { x: 60, y: 60 },       // Forward off side
      midOff: { x: 60, y: 100 },     // Straight off side
      midOn: { x: 60, y: 180 },      // Straight leg side
      midWicket: { x: 60, y: 220 },  // Forward leg side
      squareLeg: { x: 200, y: 240 }, // Square leg side
      point: { x: 200, y: 55 },      // Square off side
      thirdMan: { x: 340, y: 70 },   // Behind, off side
      fineLeg: { x: 340, y: 210 },   // Behind, leg side
      longOn: { x: 50, y: 180 },     // Deep leg side
      longOff: { x: 50, y: 100 },    // Deep off side
      slip: { x: 330, y: 100 },      // Behind wicket, off
      gully: { x: 310, y: 80 },      // Behind square, off
      keeper: { x: 320, y: 140 }     // Behind stumps
    };

    let pos = directionMap[shotDir] || { x: 150, y: 140 };

    // Adjust for boundaries - push towards edge but stay in bounds
    if (result === 'FOUR' || result === 'SIX') {
      // Push towards boundary edge
      if (pos.x > 200) pos.x = Math.min(pos.x + 25, BOUNDARY.right - 5);
      else pos.x = Math.max(pos.x - 25, BOUNDARY.left + 5);
      if (pos.y > 140) pos.y = Math.min(pos.y + 20, BOUNDARY.bottom - 5);
      else pos.y = Math.max(pos.y - 20, BOUNDARY.top + 5);
    }

    // Clamp to boundary
    pos.x = Math.max(BOUNDARY.left, Math.min(BOUNDARY.right, pos.x));
    pos.y = Math.max(BOUNDARY.top, Math.min(BOUNDARY.bottom, pos.y));

    return pos;
  }

  chaseFielder(shotDir, targetPos) {
    // Find closest fielder
    const fielderMap = {
      cover: 'cover',
      midOff: 'midOff',
      midOn: 'midOn',
      midWicket: 'midWicket',
      squareLeg: 'squareLeg',
      point: 'point',
      fineLeg: 'fineLeg',
      slip: 'slip',
      gully: 'gully'
    };

    const fielderId = fielderMap[shotDir];
    if (fielderId && this.players[fielderId]) {
      this.players[fielderId].speed = 120;
      this.players[fielderId].moveTo(targetPos.x, targetPos.y);
    }
  }

  animateRunning(runs) {
    const striker = this.players.striker;
    const nonStriker = this.players.nonStriker;

    striker.speed = 100;
    nonStriker.speed = 100;

    // Run sequence
    const runSequence = (runsLeft, strikerAtCrease) => {
      if (runsLeft <= 0) return;

      if (strikerAtCrease) {
        // Striker runs to non-striker end
        striker.moveTo(FIELD_POSITIONS.nonStrikerCrease.x, FIELD_POSITIONS.nonStrikerCrease.y);
        nonStriker.moveTo(FIELD_POSITIONS.strikerCrease.x, FIELD_POSITIONS.strikerCrease.y, () => {
          runSequence(runsLeft - 1, false);
        });
      } else {
        // Return
        striker.moveTo(FIELD_POSITIONS.strikerCrease.x, FIELD_POSITIONS.strikerCrease.y);
        nonStriker.moveTo(FIELD_POSITIONS.nonStrikerCrease.x, FIELD_POSITIONS.nonStrikerCrease.y, () => {
          runSequence(runsLeft - 1, true);
        });
      }
    };

    runSequence(runs, true);
  }

  showFlash(text, color) {
    this.flashText = text;
    this.flashColor = color;
    this.flashTimer = 1000;

    // Trigger crowd excitement based on event
    if (text === '6') {
      this.crowdExcitement = 1.0; // Maximum excitement for six
    } else if (text === '4') {
      this.crowdExcitement = 0.8;
    } else if (text === 'W') {
      this.crowdExcitement = 0.9; // Wicket - big reaction
    } else if (text !== '0' && text !== '·') {
      this.crowdExcitement = Math.max(this.crowdExcitement, 0.3);
    }
  }

  resetAfterBall() {
    this.isAnimating = false;
    this.wicketIntact = true;
    this.currentBallData = null;

    // Reset ball state
    this.ball.visible = false;
    this.ball.phase = 'idle';
    this.ball.progress = 0;

    // Smooth return to positions using moveTo (players walk back)
    this.players.bowler.speed = 80;
    this.players.bowler.moveTo(FIELD_POSITIONS.bowlerStart.x, FIELD_POSITIONS.bowlerStart.y);

    // Batsmen return to creases smoothly
    this.players.striker.speed = 60;
    this.players.striker.moveTo(FIELD_POSITIONS.strikerCrease.x, FIELD_POSITIONS.strikerCrease.y, () => {
      this.players.striker.direction = 'left';
      this.players.striker.animator.play('batReady'); // Return to batting stance with bat
    });

    this.players.nonStriker.speed = 60;
    this.players.nonStriker.moveTo(FIELD_POSITIONS.nonStrikerCrease.x, FIELD_POSITIONS.nonStrikerCrease.y, () => {
      this.players.nonStriker.direction = 'right';
      this.players.nonStriker.animator.play('idleRight');
    });

    // Fielders return to positions smoothly
    Object.keys(FIELD_POSITIONS).forEach(pos => {
      if (this.players[pos] && !['bowlerStart', 'bowlerEnd', 'bowlerWicket', 'strikerWicket', 'strikerCrease', 'nonStrikerCrease', 'keeper'].includes(pos)) {
        const p = FIELD_POSITIONS[pos];
        this.players[pos].speed = 50;
        this.players[pos].moveTo(p.x, p.y, () => {
          this.players[pos].direction = p.x > 200 ? 'left' : 'right';
          this.players[pos].animator.play(p.x > 200 ? 'idleLeft' : 'idleRight');
        });
      }
    });
  }

  gameLoop(currentTime) {
    const deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.render();

    this.animationFrame = requestAnimationFrame((t) => this.gameLoop(t));
  }

  update(deltaTime) {
    // Update all players
    Object.values(this.players).forEach(player => {
      player.update(deltaTime);
    });

    // Update ball using state machine
    if (this.ball.visible && this.ball.phase !== 'idle') {
      this.ball.progress += deltaTime / this.ball.duration;

      if (this.ball.progress >= 1) {
        this.ball.progress = 1;
        this.ball.x = this.ball.targetX;
        this.ball.y = this.ball.targetY;

        // Handle phase completion
        if (this.ball.phase === 'bowling') {
          this.ball.phase = 'idle';
          this.onBallReachedBatsman();
        } else if (this.ball.phase === 'toFielder' || this.ball.phase === 'toBoundary') {
          this.ball.phase = 'idle';
        }
      } else {
        // Smooth interpolation with easing
        const t = this.ball.progress;
        const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease-in-out

        this.ball.x = this.ball.startX + (this.ball.targetX - this.ball.startX) * ease;
        this.ball.y = this.ball.startY + (this.ball.targetY - this.ball.startY) * ease;

        // Clamp ball to boundary
        this.ball.x = Math.max(BOUNDARY.left, Math.min(BOUNDARY.right, this.ball.x));
        this.ball.y = Math.max(BOUNDARY.top, Math.min(BOUNDARY.bottom, this.ball.y));
      }
    }

    // Update flash timer
    if (this.flashTimer > 0) {
      this.flashTimer -= deltaTime;
    }

    // Update crowd animation
    this.gameTime += deltaTime;
    // Crowd excitement decays over time
    if (this.crowdExcitement > 0) {
      this.crowdExcitement = Math.max(0, this.crowdExcitement - deltaTime / 2000);
    }

    // Update banner animation
    this.bannerOffset += deltaTime / 50;

    // Update celebration timer
    if (this.celebrationTimer > 0) {
      this.celebrationTimer -= deltaTime;
      if (this.celebrationTimer <= 0) {
        this.celebration = null;
      }
    }

    // Update easter egg timer
    if (this.easterEggTimer > 0) {
      this.easterEggTimer -= deltaTime;
      if (this.easterEggTimer <= 0) {
        this.easterEgg = null;
      }
    }

    // Random easter egg chance (very rare)
    if (!this.easterEgg && Math.random() < 0.00005) {
      this.triggerEasterEgg();
    }
  }

  triggerCelebration(type, player) {
    this.celebration = { type, player };
    this.celebrationTimer = 2000;

    // Track highlights
    if (type === 'six' || type === 'wicket' || type === 'fifty') {
      this.highlights.push({
        type,
        player,
        time: this.gameTime,
        matchProgress: this.matchState?.matchProgress || 0
      });
    }
  }

  triggerEasterEgg() {
    const eggs = ['streaker', 'pigeon', 'beachball', 'cat'];
    this.easterEgg = {
      type: eggs[Math.floor(Math.random() * eggs.length)],
      x: -50,
      y: 150 + Math.random() * 100
    };
    this.easterEggTimer = 4000;
  }

  render() {
    const ctx = this.ctx;
    const s = CANVAS_SCALE;

    // Clear entire canvas with car park color
    ctx.fillStyle = CARPARK_COLOR;
    ctx.fillRect(0, 0, this.width, this.height);

    // Draw exterior to fill ENTIRE canvas (sky, buildings, car park)
    this.drawExteriorFullCanvas(ctx);

    // Now translate and scale for the game area (stadium)
    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    // Draw stadium background on top of exterior
    this.drawStadiumBackground(ctx, s);

    // Draw playing field (curved at bottom)
    this.drawOutfield(ctx, s);

    // Draw grass stripes (isometric angle) - clipped to outfield
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s;
    const leftEdge = sideStandWidth + exteriorGap;
    const rightEdge = this.gameWidth - sideStandWidth - exteriorGap;
    const topEdge = standDepth + exteriorGap;
    const bottomEdge = this.gameHeight - 20 * s - exteriorGap;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(leftEdge, topEdge);
    ctx.lineTo(rightEdge, topEdge);
    ctx.lineTo(rightEdge, bottomEdge);
    ctx.quadraticCurveTo(this.gameWidth / 2, this.gameHeight + 10 * s, leftEdge, bottomEdge);
    ctx.closePath();
    ctx.clip();

    ctx.fillStyle = GRASS_DARK;
    for (let i = -15; i < 40; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 25 * s, 0);
      ctx.lineTo((i * 25 + 300) * s, this.gameHeight);
      ctx.lineTo((i * 25 + 312) * s, this.gameHeight);
      ctx.lineTo((i * 25 + 12) * s, 0);
      ctx.fill();
    }
    ctx.restore();

    // Draw stadium stands and crowd (on top of grass edges)
    this.drawStadium(ctx, s);

    // Draw pitch - centered between the two wickets
    const pitchX = 100 * s;
    const pitchY = 110 * s;
    const pitchW = 200 * s;
    const pitchH = 60 * s;

    ctx.fillStyle = PITCH_COLOR;
    ctx.fillRect(pitchX, pitchY, pitchW, pitchH);

    ctx.strokeStyle = PITCH_DARK;
    ctx.lineWidth = 2;
    ctx.strokeRect(pitchX, pitchY, pitchW, pitchH);

    // Draw creases (popping crease - where batsman stands)
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    // Bowler's crease
    ctx.beginPath();
    ctx.moveTo(135 * s, 115 * s);
    ctx.lineTo(135 * s, 165 * s);
    ctx.stroke();
    // Batsman's crease
    ctx.beginPath();
    ctx.moveTo(265 * s, 115 * s);
    ctx.lineTo(265 * s, 165 * s);
    ctx.stroke();

    // Draw wickets (behind the crease line)
    this.drawWicket(ctx, 125 * s, 125 * s, true); // Bowler's end
    this.drawWicket(ctx, 275 * s, 125 * s, this.wicketIntact); // Batsman's end (striker defends this)

    // Draw fielders (back to front for depth)
    const playerOrder = [
      'fineLeg', 'midOn', 'midWicket', 'squareLeg',
      'midOff', 'cover', 'point', 'gully', 'slip',
      'nonStriker', 'bowler', 'keeper', 'striker'
    ];

    playerOrder.forEach(id => {
      if (this.players[id]) {
        this.drawPlayer(ctx, this.players[id]);
      }
    });

    // Draw ball (smaller)
    if (this.ball.visible) {
      ctx.drawImage(
        this.ballSprite,
        this.ball.x * s - 6,
        this.ball.y * s - 6,
        12, 12
      );
    }

    // Draw flash text
    if (this.flashTimer > 0) {
      const alpha = Math.min(1, this.flashTimer / 300);
      const flashScale = 1 + (1 - alpha) * 0.5;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = this.flashColor;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 4;
      ctx.font = `bold ${48 * flashScale}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const text = this.flashText;
      const x = this.gameWidth / 2;
      const y = this.gameHeight / 3;

      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
      ctx.restore();
    }

    // Draw celebration
    if (this.celebration) {
      this.drawCelebration(ctx, s);
    }

    // Draw easter egg
    if (this.easterEgg) {
      this.drawEasterEgg(ctx, s);
    }

    // Restore the translate/scale transform
    ctx.restore();

    // Draw weather effects on top of everything (in screen space)
    this.drawWeather(ctx);
  }

  drawWeather(ctx) {
    ctx.save();

    // Sun glare effect
    if (this.sunGlare > 0) {
      const gradient = ctx.createRadialGradient(
        this.width * 0.8, this.offsetY * 0.3, 0,
        this.width * 0.8, this.offsetY * 0.3, 200
      );
      gradient.addColorStop(0, `rgba(255, 255, 200, ${this.sunGlare})`);
      gradient.addColorStop(0.5, `rgba(255, 255, 150, ${this.sunGlare * 0.3})`);
      gradient.addColorStop(1, 'rgba(255, 255, 100, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, this.width, this.height);
    }

    // Rain effect
    if (this.weatherType === 'rain') {
      ctx.strokeStyle = 'rgba(150, 180, 255, 0.4)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 100; i++) {
        const x = (Math.sin(i * 123.456 + this.gameTime / 100) * 0.5 + 0.5) * this.width;
        const y = ((this.gameTime / 10 + i * 37) % this.height);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 5, y + 15);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  drawCelebration(ctx, s) {
    const { type, player } = this.celebration;
    const progress = 1 - (this.celebrationTimer / 2000);

    ctx.save();
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';

    if (type === 'six') {
      // Batsman jumps animation
      const jumpHeight = Math.sin(progress * Math.PI) * 20;
      const batsmanX = this.gameWidth / 2;
      const batsmanY = this.gameHeight / 2 - jumpHeight;

      // Draw celebration text
      ctx.fillStyle = '#f8d800';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.font = 'bold 16px monospace';
      const celebText = 'MAXIMUM!';
      ctx.strokeText(celebText, batsmanX, batsmanY - 40);
      ctx.fillText(celebText, batsmanX, batsmanY - 40);

    } else if (type === 'wicket') {
      // Bowler celebration
      const bowlerX = this.gameWidth * 0.35;
      const bowlerY = this.gameHeight / 2;

      ctx.fillStyle = '#ff0000';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.font = 'bold 16px monospace';
      const celebText = 'HOWZAT!';
      ctx.strokeText(celebText, bowlerX, bowlerY - 50);
      ctx.fillText(celebText, bowlerX, bowlerY - 50);

    } else if (type === 'fifty') {
      // Bat raise for 50
      ctx.fillStyle = '#00ff00';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.font = 'bold 20px monospace';
      const celebText = '50 UP!';
      ctx.strokeText(celebText, this.gameWidth / 2, this.gameHeight / 3);
      ctx.fillText(celebText, this.gameWidth / 2, this.gameHeight / 3);
    }

    ctx.restore();
  }

  drawEasterEgg(ctx, s) {
    if (!this.easterEgg) return;

    const progress = 1 - (this.easterEggTimer / 4000);
    const x = progress * (this.gameWidth + 100) - 50;
    const y = this.easterEgg.y;

    ctx.save();

    if (this.easterEgg.type === 'streaker') {
      // Pink blur running across
      ctx.fillStyle = '#ffccaa';
      ctx.fillRect(x, y, 8 * s, 16 * s);
      ctx.fillStyle = '#000';
      ctx.fillRect(x + 2 * s, y, 4 * s, 4 * s); // Head
      // Censored bar
      ctx.fillStyle = '#000';
      ctx.fillRect(x - 5 * s, y + 6 * s, 18 * s, 6 * s);
      ctx.fillStyle = '#fff';
      ctx.font = '8px monospace';
      ctx.fillText('CENSORED', x - 3 * s, y + 11 * s);

    } else if (this.easterEgg.type === 'pigeon') {
      // Grey pigeon
      ctx.fillStyle = '#888899';
      ctx.fillRect(x, y, 10 * s, 6 * s); // Body
      ctx.fillStyle = '#666677';
      ctx.fillRect(x + 8 * s, y - 2 * s, 4 * s, 4 * s); // Head
      ctx.fillStyle = '#ffaa00';
      ctx.fillRect(x + 11 * s, y, 3 * s, 2 * s); // Beak
      // Wings flapping
      const wingY = Math.sin(this.gameTime / 50) * 3;
      ctx.fillStyle = '#777788';
      ctx.fillRect(x + 2 * s, y - 4 * s + wingY, 6 * s, 3 * s);

    } else if (this.easterEgg.type === 'beachball') {
      // Colorful beach ball bouncing
      const bounceY = Math.abs(Math.sin(progress * Math.PI * 4)) * 30;
      ctx.fillStyle = '#ff6666';
      ctx.beginPath();
      ctx.arc(x, y - bounceY, 12 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#6666ff';
      ctx.beginPath();
      ctx.arc(x, y - bounceY, 12 * s, 0, Math.PI);
      ctx.fill();
      ctx.fillStyle = '#ffff66';
      ctx.beginPath();
      ctx.arc(x, y - bounceY, 12 * s, Math.PI * 0.5, Math.PI * 1.5);
      ctx.fill();

    } else if (this.easterEgg.type === 'cat') {
      // Orange cat
      ctx.fillStyle = '#ff9944';
      ctx.fillRect(x, y, 12 * s, 8 * s); // Body
      ctx.fillRect(x + 10 * s, y - 4 * s, 6 * s, 8 * s); // Head
      // Ears
      ctx.fillRect(x + 10 * s, y - 6 * s, 2 * s, 3 * s);
      ctx.fillRect(x + 14 * s, y - 6 * s, 2 * s, 3 * s);
      // Tail
      ctx.fillRect(x - 6 * s, y - 2 * s + Math.sin(this.gameTime / 100) * 2, 8 * s, 2 * s);
      // Eyes
      ctx.fillStyle = '#000';
      ctx.fillRect(x + 11 * s, y - 2 * s, 2 * s, 2 * s);
      ctx.fillRect(x + 14 * s, y - 2 * s, 2 * s, 2 * s);
    }

    ctx.restore();
  }

  drawPlayer(ctx, player) {
    const s = CANVAS_SCALE;
    const frameData = player.animator.getFrameData();

    ctx.drawImage(
      player.animator.spriteSheet,
      frameData.x, frameData.y,
      frameData.width, frameData.height,
      (player.x - SPRITE_WIDTH / 2) * s,
      (player.y - SPRITE_HEIGHT) * s,
      frameData.width * s / SCALE,
      frameData.height * s / SCALE
    );
  }

  drawWicket(ctx, x, y, intact) {
    const sprite = intact ? this.wicketSprite : this.wicketBrokenSprite;
    const s = CANVAS_SCALE;
    // Smaller wickets - 24x40 display size
    ctx.drawImage(sprite, x - 12, y, 24 * s / SCALE, 40 * s / SCALE);
  }

  drawStadium(ctx, s) {
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s;

    // Inset positions
    const leftStart = exteriorGap;
    const rightEnd = this.gameWidth - exteriorGap;
    const topStart = exteriorGap;
    const bottomEnd = this.gameHeight - exteriorGap;

    // Stadium roof/back structure (darkest, behind everything)
    ctx.fillStyle = STRUCTURE_COLOR;
    ctx.fillRect(leftStart, topStart, rightEnd - leftStart, 6 * s);
    ctx.fillRect(leftStart, topStart, 4 * s, bottomEnd - topStart);
    ctx.fillRect(rightEnd - 4 * s, topStart, 4 * s, bottomEnd - topStart);

    // Top stand tiers (gradient from back to front)
    for (let tier = 0; tier < 5; tier++) {
      const tierY = topStart + 4 * s + tier * 6 * s;
      const tierHeight = 6 * s;
      ctx.fillStyle = tier % 2 === 0 ? STAND_COLOR : STAND_TIER;
      ctx.fillRect(leftStart + 4 * s, tierY, rightEnd - leftStart - 8 * s, tierHeight);
    }

    // Left stand tiers
    for (let tier = 0; tier < 4; tier++) {
      const tierX = leftStart + 4 * s + tier * 5 * s;
      const tierWidth = 5 * s;
      ctx.fillStyle = tier % 2 === 0 ? STAND_COLOR : STAND_TIER;
      ctx.fillRect(tierX, topStart + standDepth, tierWidth, bottomEnd - topStart - standDepth - 30 * s);
    }

    // Right stand tiers
    for (let tier = 0; tier < 4; tier++) {
      const tierX = rightEnd - 4 * s - (tier + 1) * 5 * s;
      const tierWidth = 5 * s;
      ctx.fillStyle = tier % 2 === 0 ? STAND_COLOR : STAND_TIER;
      ctx.fillRect(tierX, topStart + standDepth, tierWidth, bottomEnd - topStart - standDepth - 30 * s);
    }

    // Corner stands (bottom left and right)
    ctx.fillStyle = STAND_COLOR;
    ctx.beginPath();
    ctx.moveTo(leftStart, bottomEnd);
    ctx.lineTo(leftStart + sideStandWidth + 10 * s, bottomEnd);
    ctx.lineTo(leftStart + sideStandWidth, bottomEnd - 20 * s);
    ctx.lineTo(leftStart, bottomEnd - 30 * s);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(rightEnd, bottomEnd);
    ctx.lineTo(rightEnd - sideStandWidth - 10 * s, bottomEnd);
    ctx.lineTo(rightEnd - sideStandWidth, bottomEnd - 20 * s);
    ctx.lineTo(rightEnd, bottomEnd - 30 * s);
    ctx.fill();

    // Stadium structure lines (pillars/divisions)
    ctx.strokeStyle = STRUCTURE_COLOR;
    ctx.lineWidth = 2 * s;

    // Vertical pillars in top stand
    const innerWidth = rightEnd - leftStart;
    for (let i = 1; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(leftStart + i * innerWidth / 5, topStart);
      ctx.lineTo(leftStart + i * innerWidth / 5, topStart + standDepth - 3 * s);
      ctx.stroke();
    }

    // Draw crowd in stands
    this.drawCrowd(ctx, s);

    // Draw advertising banners along boundary
    this.drawBanners(ctx, s);

    // Stadium edge highlight (front of stands)
    ctx.fillStyle = STAND_LIGHT;
    ctx.fillRect(leftStart + sideStandWidth - 2 * s, topStart + standDepth - 2 * s, rightEnd - leftStart - sideStandWidth * 2 + 4 * s, 3 * s);
    ctx.fillRect(leftStart + sideStandWidth - 2 * s, topStart + standDepth - 2 * s, 3 * s, bottomEnd - topStart - standDepth - 28 * s);
    ctx.fillRect(rightEnd - sideStandWidth - 1 * s, topStart + standDepth - 2 * s, 3 * s, bottomEnd - topStart - standDepth - 28 * s);
  }

  drawCrowd(ctx, s) {
    const seed = this.matchState ? this.matchState.matchNumber : 1;
    let crowdRand = seed * 9301;

    const nextRand = () => {
      crowdRand = (crowdRand * 9301 + 49297) % 233280;
      return crowdRand / 233280;
    };

    const time = this.gameTime / 1000;
    const excitement = this.crowdExcitement;
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s;

    // Inset positions
    const leftStart = exteriorGap;
    const rightEnd = this.gameWidth - exteriorGap;
    const topStart = exteriorGap;
    const bottomEnd = this.gameHeight - exteriorGap;

    // Dense packed crowd - top stand (many rows of tightly packed people)
    const topRows = 10;
    const personSize = 3 * s;
    const rowHeight = 2.4 * s;

    for (let row = 0; row < topRows; row++) {
      const y = topStart + 5 * s + row * rowHeight;
      const crowdWidth = rightEnd - leftStart - sideStandWidth * 2;
      const peoplePerRow = Math.floor(crowdWidth / (personSize * 0.65));

      for (let i = 0; i < peoplePerRow; i++) {
        const personSeed = nextRand();
        const xOffset = (row % 2) * (personSize * 0.32);
        const x = leftStart + sideStandWidth + 5 * s + xOffset + i * (personSize * 0.65);

        this.drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, i + row * 100);
      }
    }

    // Left stand crowd (dense vertical)
    const sideCols = 6;
    for (let col = 0; col < sideCols; col++) {
      const x = leftStart + 5 * s + col * (personSize * 0.55);
      const crowdHeight = bottomEnd - topStart - standDepth - 45 * s;
      const peoplePerCol = Math.floor(crowdHeight / rowHeight);

      for (let i = 0; i < peoplePerCol; i++) {
        const personSeed = nextRand();
        const yOffset = (col % 2) * (rowHeight * 0.5);
        const y = topStart + standDepth + 8 * s + yOffset + i * rowHeight;

        this.drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, i + col * 100 + 1000);
      }
    }

    // Right stand crowd (dense vertical)
    for (let col = 0; col < sideCols; col++) {
      const x = rightEnd - 5 * s - col * (personSize * 0.55);
      const crowdHeight = bottomEnd - topStart - standDepth - 45 * s;
      const peoplePerCol = Math.floor(crowdHeight / rowHeight);

      for (let i = 0; i < peoplePerCol; i++) {
        const personSeed = nextRand();
        const yOffset = (col % 2) * (rowHeight * 0.5);
        const y = topStart + standDepth + 8 * s + yOffset + i * rowHeight;

        this.drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, i + col * 100 + 2000);
      }
    }

    // Bottom corner crowds
    for (let row = 0; row < 5; row++) {
      const y = bottomEnd - 25 * s + row * rowHeight;
      // Left corner
      for (let i = 0; i < 6 - row; i++) {
        const personSeed = nextRand();
        const x = leftStart + 5 * s + i * (personSize * 0.65);
        this.drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, i + row * 10 + 3000);
      }
      // Right corner
      for (let i = 0; i < 6 - row; i++) {
        const personSeed = nextRand();
        const x = rightEnd - 5 * s - i * (personSize * 0.65);
        this.drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, i + row * 10 + 4000);
      }
    }
  }

  drawCrowdPerson(ctx, x, y, personSeed, time, excitement, s, index) {
    // Animated bobbing - subtle
    const bobPhase = personSeed * Math.PI * 2;
    const bobSpeed = 1.5 + personSeed * 1.5;
    const bobAmount = (0.3 + excitement * 1.5) * s;

    const bobY = Math.sin(time * bobSpeed + bobPhase) * bobAmount;

    // Wave effect when excited
    const waveOffset = index * 0.15 - time * 6;
    const waveHeight = excitement * Math.max(0, Math.sin(waveOffset)) * 2.5 * s;

    const drawY = y - bobY - waveHeight;
    const drawX = x;

    // Tiny person - just colored pixels
    const size = 2.5 * s;
    const isStanding = waveHeight > 1 * s;

    // Shirt color (random from palette)
    const colorIndex = Math.floor(personSeed * CROWD_COLORS.length);
    ctx.fillStyle = CROWD_COLORS[colorIndex];

    // Body (tiny rectangle)
    const bodyHeight = isStanding ? size * 1.4 : size;
    ctx.fillRect(drawX - size * 0.4, drawY, size * 0.8, bodyHeight);

    // Head (skin tone)
    ctx.fillStyle = personSeed > 0.5 ? '#fca044' : '#d08030';
    ctx.fillRect(drawX - size * 0.3, drawY - size * 0.4, size * 0.6, size * 0.4);

    // Arms up when standing in wave
    if (isStanding && waveHeight > 1.5 * s) {
      ctx.fillStyle = CROWD_COLORS[colorIndex];
      ctx.fillRect(drawX - size * 0.7, drawY - size * 0.2, size * 0.25, size * 0.6);
      ctx.fillRect(drawX + size * 0.45, drawY - size * 0.2, size * 0.25, size * 0.6);
    }
  }

  drawBanners(ctx, s) {
    const bannerHeight = 8 * s;
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s;

    const leftStart = exteriorGap;
    const rightEnd = this.gameWidth - exteriorGap;
    const topStart = exteriorGap;
    const bottomEnd = this.gameHeight - exteriorGap;

    // Banner along top (below top stand) - main visible banner
    const topBannerY = topStart + standDepth - bannerHeight - 1 * s;
    const bannerWidth = rightEnd - leftStart - sideStandWidth * 2;
    this.drawBannerStrip(ctx, leftStart + sideStandWidth, topBannerY, bannerWidth, bannerHeight, s, true);

    // Banner along left side
    ctx.save();
    ctx.translate(leftStart + sideStandWidth, topStart + standDepth + 5 * s);
    ctx.rotate(Math.PI / 2);
    const sideHeight = bottomEnd - topStart - standDepth - 40 * s;
    this.drawBannerStrip(ctx, 0, -bannerHeight, sideHeight, bannerHeight * 0.7, s, false);
    ctx.restore();

    // Banner along right side
    ctx.save();
    ctx.translate(rightEnd - sideStandWidth, bottomEnd - 35 * s);
    ctx.rotate(-Math.PI / 2);
    this.drawBannerStrip(ctx, 0, -bannerHeight, sideHeight, bannerHeight * 0.7, s, false);
    ctx.restore();
  }

  drawBannerStrip(ctx, x, y, width, height, s, isMain) {
    const numBanners = isMain ? 5 : 3;
    const bannerWidth = width / numBanners;

    // Animated text cycling
    const cycleSpeed = 0.001;
    const textOffset = Math.floor(this.bannerOffset * cycleSpeed) % this.bannerTexts.length;

    for (let i = 0; i < numBanners; i++) {
      // Banner background with slight border
      ctx.fillStyle = '#000000';
      ctx.fillRect(x + i * bannerWidth, y - 1, bannerWidth - 1 * s, height + 2);

      ctx.fillStyle = BANNER_COLORS[i % BANNER_COLORS.length];
      ctx.fillRect(x + i * bannerWidth + 1, y, bannerWidth - 1 * s - 2, height);

      // Get animated text
      const textIndex = (i + textOffset) % this.bannerTexts.length;
      const bannerText = this.bannerTexts[textIndex];

      // Banner text with shadow
      const fontSize = isMain ? Math.floor(6 * s) : Math.floor(5 * s);
      ctx.font = `bold ${fontSize}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Text shadow
      ctx.fillStyle = '#000000';
      ctx.fillText(bannerText, x + i * bannerWidth + bannerWidth / 2 + 1, y + height / 2 + 1);

      // Text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(bannerText, x + i * bannerWidth + bannerWidth / 2, y + height / 2);
    }
  }

  drawExteriorFullCanvas(ctx) {
    // This draws the exterior to fill the ENTIRE canvas (not just game area)
    // Creates seamless car park and sky that flows around the stadium

    const seed = this.matchState ? this.matchState.matchNumber : 1;
    let extRand = seed * 7919;
    const nextRand = () => {
      extRand = (extRand * 9301 + 49297) % 233280;
      return extRand / 233280;
    };

    // Sky gradient at top
    const horizonY = this.offsetY + 24 * this.scale;
    const skyGradient = ctx.createLinearGradient(0, 0, 0, horizonY);
    skyGradient.addColorStop(0, SKY_COLOR);
    skyGradient.addColorStop(1, SKY_HORIZON);
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, this.width, horizonY + 20);

    // Distant buildings across the top
    const buildingCount = Math.ceil(this.width / 140);
    for (let i = 0; i < buildingCount; i++) {
      const buildingX = i * 140 - 30;
      const buildingWidth = 90 + nextRand() * 100;
      const buildingHeight = 100 + nextRand() * 200;

      ctx.fillStyle = BUILDING_COLORS[Math.floor(nextRand() * BUILDING_COLORS.length)];
      ctx.fillRect(buildingX, horizonY - buildingHeight, buildingWidth, buildingHeight + 30);

      // Windows
      ctx.fillStyle = nextRand() > 0.3 ? '#606080' : '#a0a060';
      const windowRows = Math.floor(buildingHeight / 24);
      const windowCols = Math.floor(buildingWidth / 20);
      for (let wy = 0; wy < windowRows; wy++) {
        for (let wx = 0; wx < windowCols; wx++) {
          if (nextRand() > 0.4) {
            ctx.fillRect(buildingX + 8 + wx * 20, horizonY - buildingHeight + 12 + wy * 24, 10, 14);
          }
        }
      }
    }

    // Car park fills everything below horizon
    ctx.fillStyle = CARPARK_COLOR;
    ctx.fillRect(0, horizonY, this.width, this.height - horizonY);

    // Parking lines across entire car park
    ctx.strokeStyle = CARPARK_LINE;
    ctx.lineWidth = 1;
    for (let px = 0; px < this.width; px += 36) {
      ctx.beginPath();
      ctx.moveTo(px, horizonY + 10);
      ctx.lineTo(px, this.height);
      ctx.stroke();
    }

    // Parked cars scattered around (avoiding where stadium will be)
    const stadiumLeft = this.offsetX;
    const stadiumRight = this.offsetX + this.gameWidth * this.scale;
    const stadiumTop = this.offsetY;
    const stadiumBottom = this.offsetY + this.gameHeight * this.scale;

    // Cars in top area (if there's space above stadium)
    if (stadiumTop > horizonY + 30) {
      for (let c = 0; c < 15; c++) {
        const carX = nextRand() * this.width;
        const carY = horizonY + 15 + nextRand() * (stadiumTop - horizonY - 40);
        this.drawCarFullCanvas(ctx, carX, carY, CAR_COLORS[Math.floor(nextRand() * CAR_COLORS.length)], nextRand() > 0.5);
      }
    }

    // Cars on left side
    if (stadiumLeft > 40) {
      for (let c = 0; c < 10; c++) {
        const carX = 10 + nextRand() * (stadiumLeft - 30);
        const carY = horizonY + 20 + nextRand() * (this.height - horizonY - 40);
        this.drawCarFullCanvas(ctx, carX, carY, CAR_COLORS[Math.floor(nextRand() * CAR_COLORS.length)], nextRand() > 0.5);
      }
    }

    // Cars on right side
    if (this.width - stadiumRight > 40) {
      for (let c = 0; c < 10; c++) {
        const carX = stadiumRight + 10 + nextRand() * (this.width - stadiumRight - 30);
        const carY = horizonY + 20 + nextRand() * (this.height - horizonY - 40);
        this.drawCarFullCanvas(ctx, carX, carY, CAR_COLORS[Math.floor(nextRand() * CAR_COLORS.length)], nextRand() > 0.5);
      }
    }

    // Cars at bottom
    if (this.height - stadiumBottom > 40) {
      for (let c = 0; c < 15; c++) {
        const carX = nextRand() * this.width;
        const carY = stadiumBottom + 10 + nextRand() * (this.height - stadiumBottom - 30);
        this.drawCarFullCanvas(ctx, carX, carY, CAR_COLORS[Math.floor(nextRand() * CAR_COLORS.length)], nextRand() > 0.5);
      }
    }

    // Trees scattered around edges
    const treePositions = [];
    for (let t = 0; t < 12; t++) {
      let tx, ty;
      const side = Math.floor(nextRand() * 4);
      if (side === 0 && stadiumLeft > 50) {
        tx = 15 + nextRand() * (stadiumLeft - 40);
        ty = horizonY + 30 + nextRand() * (this.height - horizonY - 50);
      } else if (side === 1 && this.width - stadiumRight > 50) {
        tx = stadiumRight + 15 + nextRand() * (this.width - stadiumRight - 40);
        ty = horizonY + 30 + nextRand() * (this.height - horizonY - 50);
      } else if (side === 2 && stadiumTop > horizonY + 50) {
        tx = nextRand() * this.width;
        ty = horizonY + 20 + nextRand() * (stadiumTop - horizonY - 40);
      } else if (this.height - stadiumBottom > 50) {
        tx = nextRand() * this.width;
        ty = stadiumBottom + 20 + nextRand() * (this.height - stadiumBottom - 40);
      }
      if (tx !== undefined) {
        this.drawTreeFullCanvas(ctx, tx, ty, nextRand);
      }
    }

    // Floodlights at corners
    this.drawFloodlightFullCanvas(ctx, 10, horizonY - 15);
    this.drawFloodlightFullCanvas(ctx, this.width - 20, horizonY - 15);
  }

  drawCarFullCanvas(ctx, x, y, color, horizontal) {
    const w = horizontal ? 72 : 36;
    const h = horizontal ? 36 : 60;

    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);

    // Car roof/cabin (slightly darker)
    ctx.fillStyle = '#203040';
    if (horizontal) {
      ctx.fillRect(x + 16, y + 6, 24, 24);
      ctx.fillRect(x + 44, y + 6, 16, 24);
    } else {
      ctx.fillRect(x + 6, y + 10, 24, 16);
      ctx.fillRect(x + 6, y + 32, 24, 16);
    }

    // Wheels
    ctx.fillStyle = '#1a1a1a';
    if (horizontal) {
      ctx.fillRect(x + 4, y - 4, 12, 8);
      ctx.fillRect(x + 4, y + h - 4, 12, 8);
      ctx.fillRect(x + w - 16, y - 4, 12, 8);
      ctx.fillRect(x + w - 16, y + h - 4, 12, 8);
    } else {
      ctx.fillRect(x - 4, y + 8, 8, 12);
      ctx.fillRect(x + w - 4, y + 8, 8, 12);
      ctx.fillRect(x - 4, y + h - 20, 8, 12);
      ctx.fillRect(x + w - 4, y + h - 20, 8, 12);
    }
  }

  drawTreeFullCanvas(ctx, x, y, nextRand) {
    ctx.fillStyle = TREE_TRUNK;
    ctx.fillRect(x - 8, y, 20, 56);

    const leafColor = TREE_LEAVES[Math.floor(nextRand() * TREE_LEAVES.length)];
    ctx.fillStyle = leafColor;
    ctx.fillRect(x - 32, y - 32, 68, 40);
    ctx.fillRect(x - 24, y - 56, 52, 32);
    ctx.fillRect(x - 16, y - 72, 36, 24);
  }

  drawFloodlightFullCanvas(ctx, x, y) {
    ctx.fillStyle = '#505060';
    ctx.fillRect(x, y, 20, 320);

    ctx.fillStyle = '#707080';
    ctx.fillRect(x - 16, y - 16, 52, 28);

    ctx.fillStyle = '#ffffc0';
    ctx.fillRect(x - 8, y - 8, 16, 16);
    ctx.fillRect(x + 16, y - 8, 16, 16);
  }

  drawExterior(ctx, s) {
    // Seeded random for consistent exterior
    const seed = this.matchState ? this.matchState.matchNumber : 1;
    let extRand = seed * 7919;
    const nextRand = () => {
      extRand = (extRand * 9301 + 49297) % 233280;
      return extRand / 233280;
    };

    // Sky gradient
    const skyGradient = ctx.createLinearGradient(0, 0, 0, this.height * 0.4);
    skyGradient.addColorStop(0, SKY_COLOR);
    skyGradient.addColorStop(1, SKY_HORIZON);
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, this.width, this.height);

    // Distant buildings/cityscape on horizon
    const horizonY = 12 * s;
    const buildingCount = 20;

    for (let i = 0; i < buildingCount; i++) {
      const buildingX = (i / buildingCount) * this.width - 10 * s;
      const buildingWidth = (15 + nextRand() * 25) * s;
      const buildingHeight = (20 + nextRand() * 45) * s;

      // Building body
      ctx.fillStyle = BUILDING_COLORS[Math.floor(nextRand() * BUILDING_COLORS.length)];
      ctx.fillRect(buildingX, horizonY - buildingHeight, buildingWidth, buildingHeight + 5 * s);

      // Windows (tiny lit squares)
      ctx.fillStyle = nextRand() > 0.3 ? '#606080' : '#a0a060';
      const windowRows = Math.floor(buildingHeight / (6 * s));
      const windowCols = Math.floor(buildingWidth / (5 * s));
      for (let wy = 0; wy < windowRows; wy++) {
        for (let wx = 0; wx < windowCols; wx++) {
          if (nextRand() > 0.4) {
            ctx.fillRect(
              buildingX + 2 * s + wx * 5 * s,
              horizonY - buildingHeight + 3 * s + wy * 6 * s,
              2 * s, 3 * s
            );
          }
        }
      }
    }

    // Car park area (fills lower part behind stadium)
    ctx.fillStyle = CARPARK_COLOR;
    ctx.fillRect(0, horizonY, this.width, this.height - horizonY);

    // Parking lines
    ctx.strokeStyle = CARPARK_LINE;
    ctx.lineWidth = 1;
    for (let px = 0; px < this.width; px += 18 * s) {
      ctx.beginPath();
      ctx.moveTo(px, horizonY + 5 * s);
      ctx.lineTo(px, this.height);
      ctx.stroke();
    }

    // Parked cars (scattered around edges)
    const carAreas = [
      { x: 0, y: horizonY + 8 * s, w: 25 * s, h: this.height - horizonY - 10 * s },  // Left
      { x: this.width - 25 * s, y: horizonY + 8 * s, w: 25 * s, h: this.height - horizonY - 10 * s },  // Right
      { x: 30 * s, y: horizonY + 2 * s, w: this.width - 60 * s, h: 12 * s },  // Top row
    ];

    carAreas.forEach(area => {
      const carsInArea = Math.floor((area.w * area.h) / (200 * s * s));
      for (let c = 0; c < carsInArea; c++) {
        const carX = area.x + nextRand() * (area.w - 12 * s);
        const carY = area.y + nextRand() * (area.h - 6 * s);
        this.drawCar(ctx, carX, carY, s, CAR_COLORS[Math.floor(nextRand() * CAR_COLORS.length)], nextRand() > 0.5);
      }
    });

    // Trees scattered around
    const treePositions = [
      { x: 8 * s, y: horizonY + 15 * s },
      { x: 20 * s, y: horizonY + 35 * s },
      { x: this.width - 12 * s, y: horizonY + 20 * s },
      { x: this.width - 22 * s, y: horizonY + 40 * s },
      { x: 60 * s, y: horizonY + 6 * s },
      { x: this.width - 70 * s, y: horizonY + 8 * s },
    ];

    treePositions.forEach(pos => {
      this.drawTree(ctx, pos.x, pos.y, s, nextRand);
    });

    // Floodlight towers at corners
    this.drawFloodlight(ctx, 3 * s, horizonY - 10 * s, s);
    this.drawFloodlight(ctx, this.width - 8 * s, horizonY - 10 * s, s);
  }

  drawCar(ctx, x, y, s, color, horizontal) {
    const w = horizontal ? 10 * s : 5 * s;
    const h = horizontal ? 5 * s : 8 * s;

    // Car body
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);

    // Windows (darker)
    ctx.fillStyle = '#203040';
    if (horizontal) {
      ctx.fillRect(x + 2 * s, y + 1 * s, 3 * s, 3 * s);
      ctx.fillRect(x + 6 * s, y + 1 * s, 2 * s, 3 * s);
    } else {
      ctx.fillRect(x + 1 * s, y + 1 * s, 3 * s, 2 * s);
      ctx.fillRect(x + 1 * s, y + 4 * s, 3 * s, 2 * s);
    }
  }

  drawTree(ctx, x, y, s, nextRand) {
    // Trunk
    ctx.fillStyle = TREE_TRUNK;
    ctx.fillRect(x - 1 * s, y, 3 * s, 8 * s);

    // Leaves (layered circles)
    const leafColor = TREE_LEAVES[Math.floor(nextRand() * TREE_LEAVES.length)];
    ctx.fillStyle = leafColor;

    // Simple pixel tree top
    ctx.fillRect(x - 4 * s, y - 4 * s, 9 * s, 5 * s);
    ctx.fillRect(x - 3 * s, y - 7 * s, 7 * s, 4 * s);
    ctx.fillRect(x - 2 * s, y - 9 * s, 5 * s, 3 * s);
  }

  drawFloodlight(ctx, x, y, s) {
    // Tower (thin pole)
    ctx.fillStyle = '#505060';
    ctx.fillRect(x, y, 3 * s, 50 * s);

    // Light fixture at top
    ctx.fillStyle = '#707080';
    ctx.fillRect(x - 2 * s, y - 2 * s, 7 * s, 4 * s);

    // Lights (bright spots)
    ctx.fillStyle = '#ffffc0';
    ctx.fillRect(x - 1 * s, y - 1 * s, 2 * s, 2 * s);
    ctx.fillRect(x + 2 * s, y - 1 * s, 2 * s, 2 * s);
  }

  drawStadiumBackground(ctx, s) {
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s; // Gap to show car park

    // Stadium outer wall - inset from edges to show exterior
    ctx.fillStyle = STAND_DARK;

    // Top section (inset from top)
    ctx.fillRect(sideStandWidth + exteriorGap, exteriorGap, this.gameWidth - sideStandWidth * 2 - exteriorGap * 2, standDepth);

    // Left section (inset from left)
    ctx.fillRect(exteriorGap, exteriorGap, sideStandWidth, this.gameHeight - exteriorGap * 2 - 20 * s);

    // Right section (inset from right)
    ctx.fillRect(this.gameWidth - sideStandWidth - exteriorGap, exteriorGap, sideStandWidth, this.gameHeight - exteriorGap * 2 - 20 * s);

    // Bottom corners (smaller, inset)
    ctx.beginPath();
    ctx.moveTo(exteriorGap, this.gameHeight - 50 * s);
    ctx.lineTo(sideStandWidth + exteriorGap + 15 * s, this.gameHeight - exteriorGap);
    ctx.lineTo(exteriorGap, this.gameHeight - exteriorGap);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(this.gameWidth - exteriorGap, this.gameHeight - 50 * s);
    ctx.lineTo(this.gameWidth - sideStandWidth - exteriorGap - 15 * s, this.gameHeight - exteriorGap);
    ctx.lineTo(this.gameWidth - exteriorGap, this.gameHeight - exteriorGap);
    ctx.fill();
  }

  drawOutfield(ctx, s) {
    const standDepth = 38 * s;
    const sideStandWidth = 32 * s;
    const exteriorGap = 12 * s;

    // Adjusted positions for inset stadium
    const leftEdge = sideStandWidth + exteriorGap;
    const rightEdge = this.gameWidth - sideStandWidth - exteriorGap;
    const topEdge = standDepth + exteriorGap;
    const bottomEdge = this.gameHeight - 20 * s - exteriorGap;

    // Green outfield (fills the playing area)
    ctx.fillStyle = GRASS_LIGHT;
    ctx.beginPath();
    ctx.moveTo(leftEdge, topEdge);
    ctx.lineTo(rightEdge, topEdge);
    ctx.lineTo(rightEdge, bottomEdge);
    ctx.quadraticCurveTo(this.gameWidth / 2, this.gameHeight + 10 * s, leftEdge, bottomEdge);
    ctx.closePath();
    ctx.fill();

    // Boundary rope (curved at bottom)
    ctx.strokeStyle = BOUNDARY_COLOR;
    ctx.lineWidth = 2 * s;
    ctx.setLineDash([6 * s, 3 * s]);
    ctx.beginPath();
    ctx.moveTo(leftEdge + 8 * s, topEdge + 8 * s);
    ctx.lineTo(rightEdge - 8 * s, topEdge + 8 * s);
    ctx.lineTo(rightEdge - 8 * s, bottomEdge - 8 * s);
    ctx.quadraticCurveTo(this.gameWidth / 2, this.gameHeight - 5 * s, leftEdge + 8 * s, bottomEdge - 8 * s);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
  }

  destroy() {
    this.clearAllTimeouts();
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}

// Factory function
function createCricketGame(canvasElement) {
  return new CricketGame(canvasElement);
}

export { CricketGame, createCricketGame };
